'use client'

import Pagination from "components/pagination";
import { useEffect, useState } from "react";
import useTranslation from "../../hooks/useTranslate.hook";
import NoData from "../no-data";
import style from "./style.module.scss";
import CheckboxV3 from "components/checkboxs/v3";
import { useSearchParams } from "next/navigation";
import ValidationHelper from "@/utils/validation.util";
import SvgCheckboxMinus from "components/svgs/checkbox-minus";
import TooltipV2 from "../tooltip-v2";
import { useIsClient } from "@/hooks/useIsClient.hook";

export type ColumnType = {
    title: string;
    name: string;
    render?: Function;
    key: string;
    colClassName?: string;
    hideOn?: Array<any>;
    isSortable?: boolean;
    isNumeric?: boolean;
}

type TableType = {
    data: any;
    columns: Array<ColumnType>,
    rowKey: string;
    checkbox?: boolean;
    pagination?: {enabled: boolean, total: number, pageSize: number, currentPage: number};
    onCheckboxChange?: Function;
    onPaginationChange?: Function;
    rowCustomFunction?: (data?: any) => void;
    noDataTextkey?: string;
    compositeKey?: Array<any>;
    defaultCheckedRowKeys?: any;
    isLoading?: boolean;
    disabledIds?: Array<number>;
    disabledHoverText?: string
}

const Table = ({data, checkbox = false, columns, rowKey, pagination, onCheckboxChange, onPaginationChange, rowCustomFunction, noDataTextkey = "no_data", compositeKey, defaultCheckedRowKeys, isLoading = false, disabledIds = [], disabledHoverText }: TableType) => {
    
    const { t } = useTranslation();
    const searchParams = useSearchParams()
    const [ checkMain, setCheckMain ] = useState(false);
    const [ checkedRows, setCheckedRows ] = useState<any>([]);
    const isClient = useIsClient();

    const handleCheckAll = (checked: boolean) => {
        // POTENTIAL ISSUES BUT WORKS FOR NOW
        if (checkedRows.length === 0 && !ValidationHelper.isArrayEmptyOrNotArray(disabledIds)) {
            let checkedData: Array<any> = [];
            data.map((item: any) => { return disabledIds.find((disabledId: number) => disabledId === item.id) ? false : checkedData.push(item)});
            onCheckboxChange && onCheckboxChange(checkedData);
            setCheckedRows(checkedData);
        } 

        if (checkedRows.length === 0 && ValidationHelper.isArrayEmptyOrNotArray(disabledIds)) {
            let checkedData: Array<any> = [];
            data.map((item: any) => { return checkedData.push(item) });
            onCheckboxChange && onCheckboxChange(checkedData);
            setCheckedRows(checkedData);
        } 

        if (checkedRows.length > 0) {
            onCheckboxChange && onCheckboxChange([]);
            setCheckedRows([]);
        }
    };

    const handleCheckRow = (value: any) => {
        if (!checkedRows.includes(value)) {
            onCheckboxChange && onCheckboxChange([...checkedRows, value]);
            setCheckedRows([...checkedRows, value]);
        } else {
            const filtered: Array<any> = checkedRows.filter((item: any) => item !== value);
            onCheckboxChange && onCheckboxChange(filtered);
            setCheckedRows(filtered);
        }
    }

    useEffect(() => {
        if (ValidationHelper.isArrayEmptyOrNotArray(disabledIds)) {
            if (checkedRows?.length === 0) {
                setCheckMain(false);
            }
            if (checkedRows?.length === data?.length) {
                setCheckMain(true);
            } else {
                setCheckMain(false);
            }
        } else {
            if (checkedRows?.length === (data?.length - disabledIds?.length)) {
                setCheckMain(true);
            } else {
                setCheckMain(false);
            }
        }
    }, [checkedRows, data]);

    useEffect(() => {
        if (checkbox) {
            onCheckboxChange && onCheckboxChange([]);
            setCheckedRows([]);
            if (!!defaultCheckedRowKeys && defaultCheckedRowKeys.length > 0) {
                let foundRows = new Array<any>();
                defaultCheckedRowKeys.forEach((defaultRowKey: any) => {
                    let foundData = data.find((dataRow: any) => dataRow[rowKey] == defaultRowKey);
                    if (!!foundData) {
                        foundRows.push(foundData);
                    }
                })
                if (foundRows.length > 0) {
                    onCheckboxChange && onCheckboxChange(foundRows);
                    setCheckedRows(foundRows);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        const parameters = searchParams && Object.fromEntries(searchParams?.entries());
    
        if (!ValidationHelper.isPropertyNullOrEmpty(parameters?.currentPage) && !ValidationHelper.isPropertyNullOrEmpty(parameters?.pageSize)) {
            if (
                pagination?.total && 
                pagination?.enabled && 
                ( Number(parameters?.currentPage) !== Number(pagination?.currentPage) )
            ) {
                onPaginationChange && onPaginationChange({
                    ...pagination, 
                    currentPage: Number(parameters?.currentPage), 
                    pageSize: Number(parameters?.pageSize) 
                })
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, data]);
    
    return (
        <div className={style.gTable}>
            <div className={`${style.gTableRow} ${style.gTableRow__head}`} key="header-row">
                { checkbox ? 
                    <div className={style.gTableCol}>
                        <CheckboxV3 
                            label="" 
                            name="checkAll" 
                            onChange={(event: any) => {
                                handleCheckAll(event.target.checked);
                            }} 
                            checked={checkMain} 
                            icon={<SvgCheckboxMinus width="24px"/>}
                        />
                    </div> : 
                null }
                {columns?.map((col: any) => {
                    const classConfig = `${style.gTableCol} ${col?.colClassName} ${col?.hideOn && col.hideOn.map((size: string) => `u-hide--${size}`).join(" ")}`;
                    return <div className={classConfig} key={col?.key}>{col?.title}</div>
                })}
            </div>
            {data?.map((item: any) => {
                const isDisabledItem = !!(!ValidationHelper.isArrayEmptyOrNotArray(disabledIds) && disabledIds.find((disabledId: number) => disabledId === item.id));
                return (
                    <div className={`${style.gTableRow} ${style.gTableRow__body} ${rowCustomFunction && rowCustomFunction(item)} ${checkbox && checkedRows.includes(item) && style.gTableRow__selected}`} key={compositeKey && compositeKey?.length > 0 ? compositeKey.map((cKey: any) => item[cKey]).join("_") : item?.id ?? item[rowKey]}>
                        { checkbox ? 
                            <div className={style.gTableCol}>
                                <TooltipV2 enable={isDisabledItem && !ValidationHelper.isPropertyNullOrEmptyV2(disabledHoverText)} text={disabledHoverText}>
                                    <CheckboxV3 
                                        label="" 
                                        name={`checkRow-${item?.id ?? item[rowKey]}`} 
                                        onChange={() => handleCheckRow(item)} checked={checkedRows.includes(item)} 
                                        disabled={!ValidationHelper.isArrayEmptyOrNotArray(disabledIds) ? ( isDisabledItem ? true : false ) : undefined} 
                                    />
                                </TooltipV2>
                                
                            </div> : 
                        null }
                        { columns?.map((col: any) => {
                            const classConfig = `${style.gTableCol} ${col?.colClassName} ${col?.hideOn && col.hideOn.map((size: string) => `u-hide--${size}`).join(" ")}`;
                            return (
                                <div className={classConfig} key={`${item?.id}-${col?.name}`}>
                                    {col.render ? col.render(item[col?.name], item[rowKey], item) : item[col?.name]}
                                </div>
                            )
                        }) }
                    </div>
                )
            })}
            {!data && !isLoading && (
                <div className="u-flex u-padding--top-32 u-flex--center-x">
                    <NoData text={t('no_data')} />
                </div>
            )}
            {isLoading && (
                <>
                    {[...Array(pagination?.pageSize ? Number(pagination?.pageSize) : 8)].map((_, index: number) => <div className={`${style.gTableRowSkeleton}`} key={index}></div>)}
                </>
            )}
            {(pagination && onPaginationChange) && (
                <div>
                    <Pagination total={pagination?.total} pageSize={pagination?.pageSize} currentPage={pagination?.currentPage} onChange={(query: any) => onPaginationChange(query)}/>
                </div>
            )}
            {data?.length === 0 && (
                <div className={`${style.gTableNoData}`}>
                    <NoData text={t(noDataTextkey)} />
                </div>
            )}
        </div>
    )
}

export default Table;
